/**
 * AANKH - Settings Panel Component
 * 
 * @author Harshit Khemani
 * @version 1.9.0
 * @description Settings modal with server configuration and setup instructions
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDevice } from '../hooks/useDevice';

/**
 * Settings Panel Modal
 */
function SettingsPanel({ isOpen, onClose }) {
  const { 
    isConnected, 
    deviceInfo, 
    deviceInformation, 
    setDeviceName,
    wifiSSID,
    wifiPassword,
    isWifiAvailable,
    wifiConnectionEnabled,
    isWifiConnected,
    ipAddress,
    setWifiSSID,
    setWifiPassword,
    toggleWifiConnection,
  } = useDevice();
  const [activeTab, setActiveTab] = useState('device');
  const [serverUrl, setServerUrl] = useState('wss://localhost');
  const [serverEnabled, setServerEnabled] = useState(false);

  // Load saved settings
  useEffect(() => {
    const savedUrl = localStorage.getItem('aankh_server_url');
    const savedEnabled = localStorage.getItem('aankh_server_enabled');
    if (savedUrl) setServerUrl(savedUrl);
    if (savedEnabled) setServerEnabled(savedEnabled === 'true');
  }, []);

  // Save settings
  const saveSettings = () => {
    localStorage.setItem('aankh_server_url', serverUrl);
    localStorage.setItem('aankh_server_enabled', serverEnabled.toString());
    console.log('[AANKH] Settings saved:', { serverUrl, serverEnabled });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
          />
          
          {/* Modal Wrapper - Flexbox centering */}
          <div className="min-h-full flex items-center justify-center p-4">
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">Settings</h2>
                </div>
                
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-100 overflow-x-auto">
                <TabButton active={activeTab === 'device'} onClick={() => setActiveTab('device')}>
                  Device
                </TabButton>
                <TabButton active={activeTab === 'wifi'} onClick={() => setActiveTab('wifi')}>
                  WiFi
                </TabButton>
                <TabButton active={activeTab === 'server'} onClick={() => setActiveTab('server')}>
                  Server
                </TabButton>
                <TabButton active={activeTab === 'setup'} onClick={() => setActiveTab('setup')}>
                  Setup
                </TabButton>
                <TabButton active={activeTab === 'about'} onClick={() => setActiveTab('about')}>
                  About
                </TabButton>
              </div>

              {/* Content - Fixed height with scroll */}
              <div className="max-h-[60vh] overflow-y-auto p-4">
                {activeTab === 'device' && (
                  <DeviceSettings 
                    isConnected={isConnected}
                    deviceInfo={deviceInfo}
                    deviceInformation={deviceInformation}
                    setDeviceName={setDeviceName}
                  />
                )}
                
                {activeTab === 'wifi' && (
                  <WifiSettings
                    isConnected={isConnected}
                    wifiSSID={wifiSSID}
                    wifiPassword={wifiPassword}
                    isWifiAvailable={isWifiAvailable}
                    wifiConnectionEnabled={wifiConnectionEnabled}
                    isWifiConnected={isWifiConnected}
                    ipAddress={ipAddress}
                    setWifiSSID={setWifiSSID}
                    setWifiPassword={setWifiPassword}
                    toggleWifiConnection={toggleWifiConnection}
                  />
                )}
                
                {activeTab === 'server' && (
                  <ServerSettings 
                    serverUrl={serverUrl}
                    setServerUrl={setServerUrl}
                    serverEnabled={serverEnabled}
                    setServerEnabled={setServerEnabled}
                  />
                )}
                
                {activeTab === 'setup' && <SetupGuide />}
                {activeTab === 'about' && <AboutSection />}
              </div>

              {/* Footer - Only for server tab */}
              {activeTab === 'server' && (
                <div className="p-4 border-t border-gray-100 bg-gray-50">
                  <button
                    onClick={() => { saveSettings(); onClose(); }}
                    className="w-full py-3 rounded-xl font-medium text-white bg-gradient-to-r from-primary to-pink-400 shadow-lg shadow-primary/25 hover:shadow-xl transition-all"
                  >
                    Save Settings
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}

/**
 * Tab button component
 */
function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors
        ${active 
          ? 'text-primary border-primary bg-primary/5' 
          : 'text-gray-500 border-transparent hover:text-gray-700'
        }`}
    >
      {children}
    </button>
  );
}

/**
 * Device settings section
 */
function DeviceSettings({ isConnected, deviceInfo, deviceInformation, setDeviceName }) {
  const [newName, setNewName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameError, setRenameError] = useState(null);

  const handleRename = async () => {
    if (!newName.trim() || !isConnected) return;
    
    setIsRenaming(true);
    setRenameError(null);
    
    try {
      await setDeviceName(newName.trim());
      setNewName('');
    } catch (error) {
      setRenameError('Failed to rename device. Please try again.');
      console.error('[AANKH] Rename error:', error);
    } finally {
      setIsRenaming(false);
    }
  };

  if (!isConnected) {
    return (
      <div className="space-y-4">
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
          <p className="text-sm text-amber-700">
            <strong>Not Connected:</strong> Connect to a device to view device information.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Device Name */}
      <div className="p-3 rounded-xl bg-white border border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-1">Device Name</label>
        <p className="text-sm text-gray-900 font-medium mb-3">{deviceInfo?.name || 'Unknown'}</p>
        
        <div className="space-y-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Enter new name (min 3 characters)"
            minLength={3}
            maxLength={20}
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary/20"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim().length >= 3) handleRename();
            }}
          />
          {renameError && (
            <p className="text-xs text-red-600">{renameError}</p>
          )}
          <button
            onClick={handleRename}
            disabled={!newName.trim() || newName.trim().length < 3 || isRenaming}
            className="w-full py-2 text-sm rounded-lg bg-primary/10 text-primary font-medium hover:bg-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isRenaming ? 'Renaming...' : 'Rename Device'}
          </button>
        </div>
      </div>

      {/* Device ID */}
      {deviceInfo?.id && (
        <div className="p-3 rounded-xl bg-white border border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-1">Device ID</label>
          <p className="text-xs text-gray-600 font-mono break-all">{deviceInfo.id}</p>
        </div>
      )}

      {/* Device Type */}
      {deviceInfo?.type && (
        <div className="p-3 rounded-xl bg-white border border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-1">Device Type</label>
          <p className="text-sm text-gray-900">{deviceInfo.type}</p>
        </div>
      )}

      {/* MTU */}
      {deviceInfo?.mtu && (
        <div className="p-3 rounded-xl bg-white border border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-1">MTU</label>
          <p className="text-sm text-gray-900">{deviceInfo.mtu} bytes</p>
        </div>
      )}

      {/* Device Information (JSON) */}
      {deviceInformation && (
        <div className="p-3 rounded-xl bg-white border border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">Device Configuration</label>
          <pre className="text-xs text-gray-600 bg-gray-50 p-3 rounded-lg overflow-x-auto max-h-48 overflow-y-auto">
            {JSON.stringify(deviceInformation, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

/**
 * Server settings section
 */
function ServerSettings({ serverUrl, setServerUrl, serverEnabled, setServerEnabled }) {
  return (
    <div className="space-y-4">
      {/* Info Banner */}
      <div className="p-3 rounded-xl bg-blue-50 border border-blue-100">
        <p className="text-xs text-blue-700">
          <strong>Optional:</strong> The local server enables WebSocket relay. Most users don't need this — Web Bluetooth works directly.
        </p>
      </div>

      {/* Enable Server Toggle */}
      <div className="p-3 rounded-xl bg-white border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900 text-sm">Local Server</p>
            <p className="text-xs text-gray-500">Connect to local SDK server</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              checked={serverEnabled}
              onChange={(e) => setServerEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
          </label>
        </div>
      </div>

      {/* Server URL */}
      <div className={`space-y-2 ${serverEnabled ? '' : 'opacity-50'}`}>
        <label className="block text-sm font-medium text-gray-700">Server URL</label>
        <input
          type="text"
          value={serverUrl}
          onChange={(e) => setServerUrl(e.target.value)}
          disabled={!serverEnabled}
          placeholder="wss://localhost"
          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary/20 disabled:bg-gray-50 disabled:cursor-not-allowed"
        />
        <div className="flex gap-2">
          <button
            onClick={() => setServerUrl('wss://localhost')}
            disabled={!serverEnabled}
            className="flex-1 py-2 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
          >
            localhost
          </button>
          <button
            onClick={() => setServerUrl('wss://bs.local')}
            disabled={!serverEnabled}
            className="flex-1 py-2 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
          >
            bs.local
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Setup guide section
 */
function SetupGuide() {
  return (
    <div className="space-y-4">
      {/* Prerequisites */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-2">Prerequisites</h3>
        <ul className="space-y-1 text-sm text-gray-600">
          <li className="flex items-center gap-2">
            <span className="text-green-500">✓</span> Omi Glass with BrilliantSole firmware
          </li>
          <li className="flex items-center gap-2">
            <span className="text-green-500">✓</span> Chrome, Edge, or Opera browser
          </li>
          <li className="flex items-center gap-2">
            <span className="text-green-500">✓</span> Bluetooth enabled
          </li>
        </ul>
      </div>

      {/* Basic Usage */}
      <div className="p-3 rounded-xl bg-green-50 border border-green-100">
        <p className="text-sm text-green-800">
          <strong>No setup needed!</strong> Click "Connect New Device" and select your glasses.
        </p>
      </div>

      {/* Server Setup */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-2">Optional: Local Server</h3>
        <p className="text-xs text-gray-500 mb-3">For advanced WebSocket/UDP features</p>
        
        <div className="space-y-2 text-sm">
          <Step n={1}>Clone SDK: <code className="bg-gray-100 px-1 rounded text-xs">git clone github.com/brilliantsole/BrilliantSole-JavaScript-SDK</code></Step>
          <Step n={2}>Install: <code className="bg-gray-100 px-1 rounded text-xs">npm install</code></Step>
          <Step n={3}>Generate SSL certs in <code className="bg-gray-100 px-1 rounded text-xs">sec/</code> folder</Step>
          <Step n={4}>Run: <code className="bg-gray-100 px-1 rounded text-xs">node server.js</code></Step>
          <Step n={5}>Enable in Settings → Server</Step>
        </div>
      </div>

      {/* Troubleshooting */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-2">Troubleshooting</h3>
        <div className="space-y-2 text-xs">
          <div className="p-2 rounded-lg bg-amber-50">
            <p className="font-medium text-amber-900">Device not found?</p>
            <p className="text-amber-700">Check Bluetooth is on and glasses are powered.</p>
          </div>
          <div className="p-2 rounded-lg bg-amber-50">
            <p className="font-medium text-amber-900">SSL error?</p>
            <p className="text-amber-700">Accept the self-signed certificate first.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({ n, children }) {
  return (
    <div className="flex gap-2 text-gray-600">
      <span className="w-5 h-5 rounded bg-primary/10 text-primary text-xs flex items-center justify-center font-bold flex-shrink-0">{n}</span>
      <span className="text-xs">{children}</span>
    </div>
  );
}

/**
 * About section
 */
function AboutSection() {
  return (
    <div className="space-y-4 text-center">
      {/* Logo */}
      <div className="py-4">
        <img src="/logo.svg" alt="AANKH" className="w-12 h-12 mx-auto mb-2" />
        <h3 className="text-xl font-bold text-gray-900">AANKH</h3>
        <p className="text-sm text-gray-500">Version 1.2.0</p>
      </div>

      {/* Description */}
      <p className="text-xs text-gray-600">
        A web interface for Omi Glass Dev Kit via Web Bluetooth using BrilliantSole SDK.
      </p>

      {/* Credits */}
      <div className="space-y-2 text-sm">
        <a href="https://github.com/HKTITAN" target="_blank" rel="noopener noreferrer" className="block p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
          <p className="font-medium text-gray-900">Harshit Khemani</p>
          <p className="text-xs text-gray-500">Creator</p>
        </a>
        <a href="https://brilliantsole.com" target="_blank" rel="noopener noreferrer" className="block p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
          <p className="font-medium text-gray-900">Zack Qattan</p>
          <p className="text-xs text-gray-500">BrilliantSole SDK</p>
        </a>
        <a href="https://omi.me" target="_blank" rel="noopener noreferrer" className="block p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
          <p className="font-medium text-gray-900">Based Hardware</p>
          <p className="text-xs text-gray-500">Omi Glass</p>
        </a>
      </div>

      {/* Links */}
      <div className="flex gap-2">
        <a 
          href="https://github.com/HKTITAN/AANKH-glass"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 py-2 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
        >
          GitHub
        </a>
        <a 
          href="https://brilliantsole.github.io/BrilliantSole-JavaScript-SDK/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 py-2 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
        >
          SDK Docs
        </a>
      </div>

      <p className="text-[10px] text-gray-400">MIT License • Open Source</p>
    </div>
  );
}

/**
 * WiFi settings section
 */
function WifiSettings({
  isConnected,
  wifiSSID,
  wifiPassword,
  isWifiAvailable,
  wifiConnectionEnabled,
  isWifiConnected,
  ipAddress,
  setWifiSSID,
  setWifiPassword,
  toggleWifiConnection,
}) {
  const [newSSID, setNewSSID] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isSettingSSID, setIsSettingSSID] = useState(false);
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [error, setError] = useState(null);

  // Update local state when props change
  useEffect(() => {
    if (wifiSSID) setNewSSID(wifiSSID);
  }, [wifiSSID]);

  const handleSetSSID = async () => {
    if (!newSSID.trim() || !isConnected) return;
    
    setIsSettingSSID(true);
    setError(null);
    
    try {
      await setWifiSSID(newSSID.trim());
      setNewSSID('');
    } catch (error) {
      setError('Failed to set WiFi SSID. Please try again.');
      console.error('[AANKH] Set SSID error:', error);
    } finally {
      setIsSettingSSID(false);
    }
  };

  const handleSetPassword = async () => {
    if (!isConnected) return;
    
    setIsSettingPassword(true);
    setError(null);
    
    try {
      await setWifiPassword(newPassword);
      setNewPassword('');
    } catch (error) {
      setError('Failed to set WiFi password. Please try again.');
      console.error('[AANKH] Set password error:', error);
    } finally {
      setIsSettingPassword(false);
    }
  };

  if (!isConnected) {
    return (
      <div className="space-y-4">
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
          <p className="text-sm text-amber-700">
            <strong>Not Connected:</strong> Connect to a device to configure WiFi settings.
          </p>
        </div>
      </div>
    );
  }

  if (!isWifiAvailable) {
    return (
      <div className="space-y-4">
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
          <p className="text-sm text-amber-700">
            <strong>WiFi Not Available:</strong> This device does not support WiFi configuration.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 rounded-xl bg-red-50 border border-red-100">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* WiFi Status */}
      <div className="p-3 rounded-xl bg-white border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">WiFi Status</label>
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
            isWifiConnected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
          }`}>
            {isWifiConnected ? 'Connected' : 'Disconnected'}
          </span>
        </div>
        {ipAddress && (
          <p className="text-xs text-gray-600 font-mono">IP: {ipAddress}</p>
        )}
      </div>

      {/* WiFi SSID */}
      <div className="p-3 rounded-xl bg-white border border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-1">WiFi SSID</label>
        <p className="text-xs text-gray-600 mb-3">{wifiSSID || 'Not set'}</p>
        
        <div className="space-y-2">
          <input
            type="text"
            value={newSSID}
            onChange={(e) => setNewSSID(e.target.value)}
            placeholder="Enter WiFi SSID"
            minLength={1}
            maxLength={32}
            disabled={wifiConnectionEnabled || isSettingSSID}
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newSSID.trim() && !wifiConnectionEnabled) handleSetSSID();
            }}
          />
          <button
            onClick={handleSetSSID}
            disabled={!newSSID.trim() || wifiConnectionEnabled || isSettingSSID}
            className="w-full py-2 text-sm rounded-lg bg-primary/10 text-primary font-medium hover:bg-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSettingSSID ? 'Setting...' : 'Set SSID'}
          </button>
        </div>
      </div>

      {/* WiFi Password */}
      <div className="p-3 rounded-xl bg-white border border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-1">WiFi Password</label>
        <p className="text-xs text-gray-600 mb-3">{wifiPassword ? '••••••••' : 'Not set'}</p>
        
        <div className="space-y-2">
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter WiFi password (optional)"
            maxLength={64}
            disabled={wifiConnectionEnabled || isSettingPassword}
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !wifiConnectionEnabled) handleSetPassword();
            }}
          />
          <button
            onClick={handleSetPassword}
            disabled={wifiConnectionEnabled || isSettingPassword}
            className="w-full py-2 text-sm rounded-lg bg-primary/10 text-primary font-medium hover:bg-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSettingPassword ? 'Setting...' : 'Set Password'}
          </button>
        </div>
      </div>

      {/* Toggle WiFi Connection */}
      <div className="p-3 rounded-xl bg-white border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="font-medium text-gray-900 text-sm">WiFi Connection</p>
            <p className="text-xs text-gray-500">Enable/disable WiFi connection</p>
          </div>
          <button
            onClick={toggleWifiConnection}
            disabled={!wifiSSID}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              wifiConnectionEnabled
                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {wifiConnectionEnabled ? 'Disable' : 'Enable'}
          </button>
        </div>
        {!wifiSSID && (
          <p className="text-xs text-amber-600 mt-2">Set SSID first to enable WiFi connection</p>
        )}
      </div>
    </div>
  );
}

export default SettingsPanel;
